import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Flex, IconError, Spacer } from '@zillow/constellation';
import { Box, Button, FieldSet, Text, TextButton } from '@zillow/constellation-10';

import PostingActions from '../../../../../actions/PostingActions';

import numberUtils from '../../../../../utils/numberUtils';

import StringConstants from '../../../../../constants/StringConstants';
import { MODES, PHONE_VERIFICATION_MODES, SOURCES, VERIFICATION_STEPS } from '../../constants';

import VerificationBodyTemplate from '../VerificationBodyTemplate';
import MaxAttemptsError from '../MaxAttemptsError';

import { StyledLegend } from '../../StyledComponents';

import type { IAPIErrorResponse, IError } from '../../../../../interfaces';
import type { MODES_VALUES, SOURCES_VALUES, VerificationType } from '../../constants';
import type { ErrorRMWProps } from '../../../../../model/ErrorRMW';
import { logError } from '@zg-rentals/log-error';

const CONFIG = {
  phone: {
    value: (val: string) => numberUtils.formatPhoneNumberInput(val),
    editButtonText: 'Edit number',
  },
  email: {
    value: (val: string) => val,
    editButtonText: 'Edit email',
  },
};

type Props = {
  contentValue: string;
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  isContentEditable: boolean;
  saveVerificationType: (type: VerificationType | MODES_VALUES) => void;
  verificationMode?: MODES_VALUES;
  verificationSource?: SOURCES_VALUES;
};

const GetVerificationCode = ({
  contentValue,
  currentStepIndex,
  incrementStep,
  isContentEditable,
  saveVerificationType,
  verificationMode = MODES.PHONE,
  verificationSource = SOURCES.LISTING_CONTACT,
}: Props) => {
  const dispatch = useDispatch();

  const [error, setError] = useState<Partial<ErrorRMWProps>>({});

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  const handleRequestPhoneVerification = (type: VerificationType) => {
    const contactObj = {
      phone: contentValue,
      contactType: type,
      verificationType: type,
      verificationSource,
    };

    dispatch(PostingActions.property.requestVerification(contactObj))
      // @ts-expect-error
      .then((result: IAPIErrorResponse) => {
        if (result.success) {
          saveVerificationType(type);
          incrementStep();
        } else if (result && result.error && result.error.message) {
          if (result.error.message.includes('Contact already verified')) {
            incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS);
          } else {
            setError({
              code: result.error.code,
              message: result.error.message,
            });
          }
        }
      })
      .catch((err: IError) => {
        if (err.message) {
          setError({ code: err.code, message: err.message });
          logError({
            error: err,
            context: 'requestVerificationCode',
          });
        }
      });
  };

  const requestVerificationCode = (type: VerificationType | MODES_VALUES) => {
    switch (type) {
      case PHONE_VERIFICATION_MODES.SMS:
      case PHONE_VERIFICATION_MODES.PHONE:
        handleRequestPhoneVerification(type as VerificationType);

        break;
      default:
        return;
    }
  };

  const renderErrorMessage = () => {
    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode={verificationMode} />;
    }

    return (
      <Spacer marginTop="sm">
        <Flex display="flex" alignItems="center" justifyContent="center">
          <IconError fontColor="red600" size="sm" marginRight="sm" />
          <Text
            textStyle="body-sm"
            css={{ color: 'text.feedback.critical.strong', lineHeight: '18px', textAlign: 'center' }}
            asChild
          >
            <p>{error.message}</p>
          </Text>
        </Flex>
      </Spacer>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - Get verification code`}
      subheading={
        <Box css={{ marginBottom: { base: '200', lg: '600' } }}>
          <Text asChild>
            <p>We&#39;ll send a one-time verification code to</p>
          </Text>
          <Text asChild>
            <p className="fs-mask">
              {`${CONFIG[verificationMode].value(contentValue)}. `}
              {isContentEditable && (
                <TextButton
                  tone="brand"
                  onClick={() => incrementStep(VERIFICATION_STEPS.ADD_CONTENT)}
                  css={{
                    fontSize: 'body-sm',
                    fontWeight: 400,
                    textDecoration: 'underline',
                    xl: {
                      fontSize: 'body',
                    },
                  }}
                >
                  {CONFIG[verificationMode].editButtonText}
                </TextButton>
              )}
            </p>
          </Text>
        </Box>
      }
    >
      {verificationMode === MODES.PHONE && (
        <FieldSet
          legend={<StyledLegend>How would you like to receive the code?</StyledLegend>}
          css={{
            margin: '{spacing.400} auto 0',
          }}
        >
          <Button
            tone="brand"
            emphasis="filled"
            disabled={isButtonDisabled}
            onClick={() => requestVerificationCode(PHONE_VERIFICATION_MODES.SMS)}
            css={{
              minWidth: '200px',
              display: 'block',
              margin: '0 auto',
            }}
          >
            Send text message
          </Button>

          <Spacer marginTop="sm" />

          <Button
            tone="brand"
            emphasis="filled"
            disabled={isButtonDisabled}
            onClick={() => requestVerificationCode(PHONE_VERIFICATION_MODES.PHONE)}
            css={{
              minWidth: '200px',
              display: 'block',
              margin: '0 auto',
            }}
          >
            Call me
          </Button>
        </FieldSet>
      )}

      {Boolean(error.message) && <Spacer marginY="xs">{renderErrorMessage()}</Spacer>}
    </VerificationBodyTemplate>
  );
};

export default GetVerificationCode;
